import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Shield, MessageSquare, FileText, Settings, ArrowLeft, AlertCircle, CheckCircle2, Calendar, Users, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface SubscriptionInfo {
  product: string;
  service: "klartext" | "chatbot" | "kompakt";
  planName: string;
  displayName: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  priceAmount: number;
  currency: string;
  interval: string;
}

const serviceIcons = {
  klartext: Shield,
  chatbot: MessageSquare,
  kompakt: FileText,
};

const serviceColors = {
  klartext: "from-green-500 to-emerald-600",
  chatbot: "from-teal-500 to-cyan-600",
  kompakt: "from-blue-500 to-indigo-600",
};

const serviceNames = {
  klartext: "Klartext-Check",
  chatbot: "AI Chatbot",
  kompakt: "Klartech Kompakt",
};

const serviceLinks = {
  klartext: "/klartext-check",
  chatbot: "/chatbot-admin",
  kompakt: "/kompakt",
};

const portalFunctions = {
  klartext: "klartext-customer-portal",
  chatbot: "chatbot-customer-portal",
  kompakt: "kompakt-customer-portal",
};

export default function AboZentrale() {
  const [subscriptions, setSubscriptions] = useState<SubscriptionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [managingService, setManagingService] = useState<string | null>(null);
  const [isInvitedMember, setIsInvitedMember] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setUser(session.user);
      await checkIfInvitedMember(session.user.id);
    } else {
      setIsLoading(false);
    }
  };

  const checkIfInvitedMember = async (userId: string) => {
    try {
      // Check if user is an invited family member
      const { data: familyMember } = await supabase
        .from("klartext_family_members")
        .select("owner_id")
        .eq("member_user_id", userId)
        .eq("status", "accepted")
        .maybeSingle();

      if (familyMember) {
        // Get owner's email
        const { data: ownerProfile } = await supabase
          .from("klartext_profiles")
          .select("email")
          .eq("id", familyMember.owner_id)
          .maybeSingle();
        
        setIsInvitedMember(true);
        setOwnerEmail(ownerProfile?.email || null);
        setIsLoading(false);
        return;
      }

      // Check if user is an invited company member
      const { data: companyMember } = await supabase
        .from("klartext_company_members")
        .select("owner_id")
        .eq("member_user_id", userId)
        .eq("status", "accepted")
        .maybeSingle();

      if (companyMember) {
        // Get owner's email
        const { data: ownerProfile } = await supabase
          .from("klartext_profiles")
          .select("email")
          .eq("id", companyMember.owner_id)
          .maybeSingle();
        
        setIsInvitedMember(true);
        setOwnerEmail(ownerProfile?.email || null);
        setIsLoading(false);
        return;
      }

      // Not an invited member, fetch subscriptions normally
      setIsInvitedMember(false);
      fetchSubscriptions();
    } catch (error) {
      console.error("Error checking member status:", error);
      // On error, still try to load subscriptions
      fetchSubscriptions();
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("subscription-center");
      
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      setSubscriptions(data.subscriptions || []);
    } catch (error: any) {
      console.error("Error fetching subscriptions:", error);
      toast({
        title: "Fehler",
        description: "Abos konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async (service: "klartext" | "chatbot" | "kompakt") => {
    setManagingService(service);
    try {
      const functionName = portalFunctions[service];
      
      // Klartext uses auth header, chatbot/kompakt use API key
      if (service === "klartext") {
        const { data, error } = await supabase.functions.invoke(functionName);
        if (error) throw error;
        if (data.error) throw new Error(data.error);
        if (data.url) window.open(data.url, "_blank");
      } else {
        // For chatbot/kompakt, we need to find the API key
        // This will open the respective service page where they can manage
        toast({
          title: "Weiterleitung",
          description: `Bitte verwalten Sie Ihr ${serviceNames[service]}-Abo auf der Service-Seite.`,
        });
        navigate(serviceLinks[service]);
      }
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Portal konnte nicht geöffnet werden.",
        variant: "destructive",
      });
    } finally {
      setManagingService(null);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatInterval = (interval: string) => {
    switch (interval) {
      case "month": return "/ Monat";
      case "year": return "/ Jahr";
      case "week": return "/ Woche";
      default: return "";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle>Anmeldung erforderlich</CardTitle>
            <CardDescription>
              Bitte melden Sie sich an, um Ihre Abos zu verwalten.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button onClick={() => navigate("/klartext-check")} className="w-full">
              Zur Anmeldung
            </Button>
            <Button variant="ghost" onClick={() => navigate("/")} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zur Startseite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show restricted access for invited members
  if (isInvitedMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/5 border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Lock className="h-8 w-8 text-amber-400" />
            </div>
            <CardTitle className="text-white">Eingeschränkter Zugriff</CardTitle>
            <CardDescription className="text-muted-foreground">
              Als eingeladenes Mitglied können Sie die Abo-Verwaltung nicht nutzen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Account-Inhaber</p>
                  <p className="text-white font-medium">{ownerEmail || "Nicht verfügbar"}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Nur der Account-Inhaber kann Abonnements verwalten, ändern oder kündigen.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate("/klartext-check")} className="w-full">
                <Shield className="h-4 w-4 mr-2" />
                Zu Klartext-Check
              </Button>
              <Button variant="ghost" onClick={() => navigate("/")} className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-white">Abo-Zentrale</h1>
          </div>
          <div className="text-sm text-muted-foreground">
            {user.email}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ihre Klartech-Abonnements
            </h2>
            <p className="text-muted-foreground text-lg">
              Verwalten Sie alle Ihre aktiven Abos an einem Ort
            </p>
          </div>

          {/* Subscriptions Grid */}
          {subscriptions.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Keine aktiven Abos
                </h3>
                <p className="text-muted-foreground mb-6">
                  Sie haben derzeit keine aktiven Abonnements bei Klartech.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button onClick={() => navigate("/klartext-check")} variant="outline">
                    <Shield className="h-4 w-4 mr-2" />
                    Klartext-Check
                  </Button>
                  <Button onClick={() => navigate("/chatbot")} variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    AI Chatbot
                  </Button>
                  <Button onClick={() => navigate("/kompakt")} variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Kompakt
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {subscriptions.map((sub, index) => {
                const Icon = serviceIcons[sub.service];
                const colorClass = serviceColors[sub.service];
                const isCanceled = sub.cancelAtPeriodEnd || sub.status === "canceled";
                
                return (
                  <Card key={index} className="bg-white/5 border-white/10 overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${colorClass}`} />
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Left: Icon & Info */}
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClass}`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-white">
                                {sub.displayName}
                              </h3>
                              {isCanceled ? (
                                <Badge variant="destructive" className="text-xs">
                                  Gekündigt
                                </Badge>
                              ) : (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Aktiv
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {serviceNames[sub.service]}
                            </p>
                          </div>
                        </div>

                        {/* Center: Price */}
                        <div className="text-center md:text-left">
                          <div className="text-2xl font-bold text-white">
                            {formatPrice(sub.priceAmount, sub.currency)}
                            <span className="text-sm font-normal text-muted-foreground ml-1">
                              {formatInterval(sub.interval)}
                            </span>
                          </div>
                        </div>

                        {/* Right: End Date & Actions */}
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {isCanceled ? "Läuft bis: " : "Nächste Zahlung: "}
                              {format(new Date(sub.currentPeriodEnd), "dd. MMMM yyyy", { locale: de })}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant={isCanceled ? "outline" : "default"}
                            onClick={() => handleManageSubscription(sub.service)}
                            disabled={managingService === sub.service}
                          >
                            {managingService === sub.service ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Settings className="h-4 w-4 mr-2" />
                            )}
                            {isCanceled ? "Abo reaktivieren" : "Abo verwalten"}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Info Section */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6 text-center">
                <Shield className="h-8 w-8 mx-auto mb-3 text-green-400" />
                <h4 className="font-semibold text-white mb-2">Sichere Zahlung</h4>
                <p className="text-sm text-muted-foreground">
                  Alle Zahlungen werden sicher über Stripe abgewickelt.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-3 text-blue-400" />
                <h4 className="font-semibold text-white mb-2">Jederzeit kündbar</h4>
                <p className="text-sm text-muted-foreground">
                  Kündigen Sie Ihr Abo jederzeit ohne versteckte Kosten.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6 text-center">
                <Settings className="h-8 w-8 mx-auto mb-3 text-purple-400" />
                <h4 className="font-semibold text-white mb-2">Volle Kontrolle</h4>
                <p className="text-sm text-muted-foreground">
                  Ändern Sie Zahlungsmethoden oder wechseln Sie den Plan.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
